import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { push } from '@lagunovsky/redux-react-router'
import { flatten, isNil, isUndefined, some, sortBy, uniq } from 'lodash'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import CityAction from 'referencial/components/city/actions/CityAction'
import i18n from 'simple-react-i18n'
import { Grid2 } from '@mui/material'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import DashboardMapPanel from 'station/components/dashboard/component/DashboardMapPanel'
import DashboardStationsPanel from 'station/components/dashboard/component/DashboardStationsPanel'
import { MAP, STATION_LIST, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import HomeAction from 'home/actions/HomeAction'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { getUserBookmarksByStationType } from 'utils/UserUtil'
import { findStationType, getBookmarks, getStationType, hasLocalisationStation } from 'utils/StationUtils'
import { getObjectLabel } from 'utils/StoreUtils'
import { getDate } from 'utils/DateUtil'
import { normalizeString } from 'utils/StringUtil'
import { hasValue } from 'utils/NumberUtil'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import StationStatisticPanel from 'station/components/dashboard/component/keyfigure/StationStatisticPanel'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import { H_PLUVIO_DASHBOARD } from 'account/constants/AccessRulesConstants'
import UserAction from 'administration/components/user/actions/UserAction'
import StationAction from 'station/actions/StationAction'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import QualityAction from 'quality/actions/QualityAction'
import ReferencialAction from 'referencial/action/ReferencialAction'
import StationFilterFields from 'station/components/search/StationFilterFields'
import NetworkAction from 'referencial/components/network/actions/NetworkAction'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'
import AddPluvioStepper from '../addPluvio/AddPluvioStepper'
import SimpleTabList from 'components/list/SimpleTabList'
import Select from 'components/forms/Select'
import { convertCoordinates, PROJECTIONS } from 'utils/mapUtils/CoordinateUtils'
import ExportFileModal from 'components/modal/ExportFileModal'
import ExportAction from 'export/actions/ExportAction'
import ToastrAction from 'toastr/actions/ToastrAction'
import { formatData } from 'utils/ExportDataUtil'
import useBoolean from 'utils/customHook/useBoolean'
import { FR } from 'referencial/constants/ReferencialConstants'

const DEFAULT_FILTER = -1
const SEARCH_VALUE_HEADERS = ['number', 'code', 'name', 'city', 'SISEeaucode', 'creationDate', 'stationTypeLabel', 'operator', 'administrator', 'referent', 'department']
const stationType = STATION_TYPE_NAME.pluviometry
const PLUVIOMETRY_HEADERS_EXPORT = ['code', 'cityCode', 'internalReferences', 'cityLabel', 'name', 'creationDate', 'closeDate', 'administrator']

const ExportModal = ({
    isOpen,
    onClose = () => {},
    filteredPluviometers = [],
}) => {
    const dispatch = useDispatch()
    const citiesIndex = useSelector(store => store.CityReducer.citiesIndex, shallowEqual)

    const [projection, setProjection] = useState()

    const getLocalisation = useCallback(pluvio => {
        if (hasLocalisationStation(pluvio)) return { x: pluvio.x, y: pluvio.y, projection: pluvio.projection }

        const city = citiesIndex[pluvio.townCode]
        if (city && (!city.countryCode || city.countryCode === FR) && hasLocalisationStation(city)) {
            return { x: city.x, y: city.y, projection: city.projection }
        }

        return {}
    }, [citiesIndex])


    const onExport = (type) => {
        const pluviometersFormated = filteredPluviometers.map(pluvio => {
            const loc = getLocalisation(pluvio)
            const usedLoc = !isNil(projection) ? convertCoordinates(loc.projection, projection, loc) : loc

            return {
                ...pluvio,
                x: { value: usedLoc.x, format: '0.0000000000', cellType: 'number' },
                y: { value: usedLoc.y, format: '0.0000000000', cellType: 'number' },
                projection: { value: projection ?? usedLoc.projection, format: '0', cellType: 'number' },
            }
        })

        if (pluviometersFormated.length) {
            const exportData = [
                {
                    ...pluviometersFormated[0],
                    headers: [...pluviometersFormated[0].headers, 'x', 'y', 'projection'],
                },
                ...pluviometersFormated.slice(1),
            ]
            dispatch(ExportAction.export(formatData(exportData), type, i18n.watchpoints))
        } else {
            dispatch(ToastrAction.warning(i18n.noDataToExport))
        }
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={onClose}
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExport('csv'),
                    }],
                    settingsContent: (
                        <Select
                            label={i18n.coordinateConversion}
                            value={projection}
                            onChange={setProjection}
                            options={PROJECTIONS}
                            keyLabel='name'
                            keyValue='sandreCode'
                        />
                    ),
                },
            ]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    filteredPluviometers: PropTypes.arrayOf(PropTypes.shape({})),
}

const PluviometryPanel = () => {
    const PLUVIOMETRY_HEADERS = ['code',
        { key: 'cityCode', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.cityCodeHeader}</span>) },
        { key: 'internalReferences', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.internalReferencesHeader}</span>) },
        'cityLabel', 'name', 'creationDate', 'closeDate', 'administrator']
    const {
        citiesIndex,
        pluviometers,
        userBookmarks,
        selectedSearchValues,
        globalResearch,
        contributorLinks,
        networkLinks,
        networks,
        accountUser,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
        pluviometers: store.PluviometryReducer.pluviometers,
        userBookmarks: store.UserReducer.userBookmarks,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        globalResearch: store.HomeReducer.globalResearch,
        contributorLinks: store.StationReducer.contributorLinks,
        networkLinks: store.PluviometryReducer.networkPluvioLink,
        networks: store.NetworkReducer.networks,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const [isOpen, setIsOpen] = useState(false)
    const [filters, setFilters] = useState(() => {
        const {
            filter: filterCode = -1,
            searchValue = '',
            network = undefined,
            filterResults = [],
            referentIds = [],
            operatorIds = [],
        } = selectedSearchValues.pluviometry || {}
        return {
            filter: filterCode,
            filterResults,
            referentIds,
            operatorIds,
            network,
            searchValue: globalResearch || searchValue,
        }
    })

    const {
        value: isExportOpen,
        setTrue: onOpenExport,
        setFalse: onCloseExport,
    } = useBoolean(false)

    const dispatch = useDispatch()

    useEffect(() => {
        if (globalResearch) {
            dispatch(HomeAction.updateGlobalResearch(''))
        }
    }, [])

    const pluviometersFormatted = useMemo(() => {
        if (pluviometers.length) {
            return sortBy(pluviometers, o => o.name ? o.name.toUpperCase() : '}').map(s => ({
                ...s,
                bookmark: getBookmarks(s.code, getUserBookmarksByStationType(userBookmarks, 'pluviometry', s.code)),
                city: { value: getObjectLabel(citiesIndex[s.townCode], 'labelWithCode') },
                name: s.name || '',
                cityCode: s.townCode,
                cityLabel: getObjectLabel(citiesIndex[s.townCode], 'name'),
                creationDate: { value: getDate(s.creationDate), sortValue: s.creationDate },
                closeDate: { value: getDate(s.closeDate), sortValue: s.closeDate },
                stationType: s.stationType ? getStationType(parseInt(s.stationType)).libelle : '',
                headers: PLUVIOMETRY_HEADERS_EXPORT,
            }))
        }
        return []
    }, [citiesIndex, pluviometers, userBookmarks])

    const containsSearchValue = useCallback((station) =>
        some(SEARCH_VALUE_HEADERS, prop => station[prop] ?
            normalizeString(station[prop].toString()).includes(normalizeString(filters.searchValue))
            : false)
    , [filters.searchValue])

    const data = useMemo(() => {
        dispatch(AdministrationAction.setSelectedSearchValues(stationType, { searchValue: filters.searchValue, filter: filters.filter, filterResults: filters.filterResults, operatorIds: filters.operatorIds, referentIds: filters.referentIds, network: filters.network }))
        const searchFiltered = pluviometersFormatted.filter(s => containsSearchValue(s))

        const defaultResult = {
            title: i18n.watchpoints,
        }

        const operatorFiltered = (() => {
            if (!filters.operatorIds?.length) return searchFiltered

            const filteredLinks = contributorLinks
                .filter(l => l.contributorType === operatorType && filters.operatorIds.includes(l.idContributor))

            return searchFiltered.filter(s => filteredLinks.some(l => l.idStation === s.id))
        })()

        const referentFiltered = (() => {
            if (!filters.referentIds?.length) return operatorFiltered

            const filteredLinks = contributorLinks
                .filter(l => l.contributorType === referentType && filters.referentIds.includes(l.idContributor))

            return operatorFiltered.filter(s => filteredLinks.some(l => l.idStation === s.id))
        })()

        const networkFiltered = !isUndefined(filters.network) ? networkLinks
            .filter(l => filters.network === l.idNetwork)
            .map(l => referentFiltered.find(s => s.id === l.idPluviometer))
            .filter(s => !!s) : referentFiltered

        if (hasValue(filters.filter) && filters.filter !== DEFAULT_FILTER && !!filters.filterResults.length) {
            return { ...defaultResult, stations: flatten(filters.filterResults.map(stationResult => networkFiltered.find(station => station.id === stationResult.id) || [])) }
        }

        return { ...defaultResult, stations: networkFiltered }
    }, [pluviometersFormatted, filters.filter, filters.filterResults, filters.referentIds, filters.operatorIds, filters.network, containsSearchValue, userBookmarks])

    useActions(() => {
        const defaultActions = {
            exportList: [{
                onClick: onOpenExport,
                label: i18n.excel,
            }],
        }
        const newActions = (accountUser.isAdmin === '1' || accountUser.metadata === '1') ? {
            ...defaultActions,
            new: () => setIsOpen(true),
        } : defaultActions
        return newActions
    }, [data])

    const pluvioNetworks = uniq(networkLinks.map(l => l.idNetwork)).map(id => networks.find(n => id === n.id)).filter(c => !isUndefined(c))

    return (
        <div style={{ marginRight: 5, paddingBottom: 100 }}>
            <Grid2 container spacing={2}>
                <Grid2 container size={12} justifyContent='flex-end'>
                    <Grid2 size={3}>
                        <StationStatisticPanel stationType={stationType} round />
                    </Grid2>
                </Grid2>
                <Grid2 size={12}>
                    <SimpleTabList
                        defaultTab={STATION_LIST}
                        tabs={[{
                            constant: STATION_LIST,
                            label: i18n.table,
                            icon: 'list',
                        },
                        {
                            constant: MAP,
                            label: i18n.map,
                            icon: 'map',
                        }]}
                        noPadding
                        headerComponent={(
                            <StationFilterFields
                                defaultFilter={filters}
                                onValidate={setFilters}
                                stationType={stationType}
                                stations={pluviometersFormatted}
                                contributorLinks={contributorLinks}
                                networklist={pluvioNetworks}
                            />
                        )}
                    >
                        {tab => (
                            <>
                                {tab === STATION_LIST && <DashboardStationsPanel stationType={stationType} data={data} headers={[{ key: 'bookmark', value: '' }, ...PLUVIOMETRY_HEADERS]} />}
                                {tab === MAP && <DashboardMapPanel stationType={stationType} data={data} />}
                            </>
                        )}
                    </SimpleTabList>
                </Grid2>
                <AddPluvioStepper
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                />
                <ExportModal
                    isOpen={isExportOpen}
                    onClose={onCloseExport}
                    filteredPluviometers={data.stations.map((s) => ({ ...s, code: { value: s.code, cellType: 'string' }, cityCode: { value: s.cityCode, cellType: 'string' } }))}
                />
            </Grid2>
        </div>
    )
}

PluviometryPanel.propTypes = {
    stationType: PropTypes.string,
}

const PluviometersDashboard = ({
}) => {
    const {
        cities,
        userBookmarks,
        sandreCodes,
        status,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        userBookmarks: store.UserReducer.userBookmarks,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        status: store.QualityReducer.status,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!componentHasHabilitations(H_PLUVIO_DASHBOARD)) {
            dispatch(push('/unauthorized'))
        }
    }, [dispatch])

    const { isLoaded, progress } = useProgressDispatch(() => {
        const promises = !userBookmarks.length ? [UserAction.fetchBookmarks] : []
        const findedStationType = findStationType(stationType)
        const promisesContributorsLinks = [...promises, () => StationAction.fetchAllContributors(findedStationType.code)]
        const promisesContributors = [...promisesContributorsLinks, ContributorAction.fetchContributors]
        const promisesStatus = !status.length ? [...promisesContributors, QualityAction.fetchStatus] : promisesContributors
        const promisesSandreCodes = !sandreCodes.length ? [...promisesStatus, ReferencialAction.fetchSandreCodes] : promisesStatus
        const promisesCities = !cities.length ? [...promisesSandreCodes, CityAction.fetchCities] : promisesSandreCodes
        const promisesNetworkLinks = [...promisesCities, PluviometryAction.fetchNetworkPluvioLink]
        const promisesNetworks = [...promisesNetworkLinks, NetworkAction.fetchNetworks]
        return promisesNetworks.map(p => dispatch(p()))
    }, [])

    useTitle(() => [{
        title: i18n[stationType],
        href: stationType,
    }, {
        title: i18n.dashboard,
        href: stationType,
    }], [stationType])

    return isLoaded ? (
        <PluviometryPanel />
    ) : (
        <Grid2 container sx={{ padding: '1rem' }}>
            <Grid2 size={12}>
                <ProgressCard progress={progress} withMessage />
            </Grid2>
        </Grid2>
    )
}

export default PluviometersDashboard
